import { switchMap, takeUntil, lastValueFrom } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';

import { open } from 'rosbag';
import { ObjectUtils } from 'src/app/utils/object';
import lz4 from 'lz4js';
import { Buffer } from 'buffer';
import { RosbagBuffer } from './rosbag-buffer';
import { scanIncorrectBoxReleaseMsgOrder } from './rosbag-reader-utils';

export function readROS1Bag(path: string, buffer: RosbagBuffer): Promise<void> {
  (window as any).openBag = open;
  (window as any).bagPath = path;

  return lastValueFrom(
    fromFetch(path).pipe(
      // Only read until the bag is destroyed (insurance).
      takeUntil(buffer.destroy$),
      switchMap((res) => res.blob()), // Grab the blob from the response
      switchMap((blob) => open(new File([blob], path))), // Open the rosbag
      switchMap((bag) => {
        // Extract start and end time from
        buffer.info.startTime = ObjectUtils.cloneObject(bag.startTime);
        buffer.info.endTime = ObjectUtils.cloneObject(bag.endTime);

        // Compute total message count
        for (const chunkInfo of bag.chunkInfos) {
          for (const connection of chunkInfo.connections) {
            buffer.info.totalNrMsgs += connection.count;
          }
        }

        // Grab all topics in bag.
        for (const key of Object.keys(bag.connections)) {
          buffer.info.topics.push({
            name: bag.connections[key].topic,
            messageType: bag.connections[key].type,
          });
        }

        // Read messages
        const decompressHandlers = {
          lz4: (buffer: Buffer, uncompressedByteLength: number) =>
            Buffer.from(
              lz4.decompress(buffer, uncompressedByteLength),
              'utf-8'
            ),
        };

        // Read messages
        buffer.readProgress$.next(0);
        return bag
          .readMessages(
            // Bag options
            {
              decompress: decompressHandlers,
            },
            // Callback for each message. Add message to buffer and update progress.
            (result) => {
              buffer.addMessage(result);
              buffer.readProgress$.next(
                (buffer.getNrBufferedMsgs() / buffer.getTotalNrMsgs()) * 100
              );
            }
          )
          .then(() => {
            scanIncorrectBoxReleaseMsgOrder(buffer);

            // Update progress to 100 % when done.
            buffer.readProgress$.next(100);
          })
          .catch((error) => {
            // Emit error event if something went wrong.
            buffer.error$.next(error);
          });
      })
    )
  );
}
